import Head from "next/head";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { GetServerSideProps } from "next";

import { SERVER } from "../../src/common/config/utils";
import HeadSection from "../../src/courses/components/detail/HeadSection";
import {
  COURSE_CERTIFICATE_IMAGE,
  COURSE_LIST_PAGE_OG_IMAGE,
} from "./../../src/courses/utils/constants";
import Experts from "../../src/bootcamps/components/list/Experts";
import authService from "../../src/common/service/authService";
import HandleJoinCourse from "../../src/courses/components/detail/HandleJoinCourse";
import { SITE_NAME } from "../../src/common/config/seo";
import PageError, {
  IOnError,
  onError,
} from "../../src/common/components/error-handlers/PageError";

const BottomBanner = dynamic(
  () => import("../../src/courses/components/detail/BottomBanner")
);
const EarnRecognition = dynamic(
  () => import("../../src/bootcamps/components/detail/EarnRecognition")
);
const DetailSection = dynamic(
  () => import("../../src/courses/components/detail/DetailSection")
);
const PopUp = dynamic(
  () => import("@packages/common/src/components/notification/pop-up/PopUp")
);

export default function ({
  course,
  error,
}: {
  course: NSCourses.ICourseDetailProps;
  error: IOnError;
}) {
  const router = useRouter();

  if (error) {
    return <PageError error={error} />;
  }

  return (
    <>
      <Head>
        <title>
          {course?.meta_title || course.name} | {SITE_NAME}
        </title>
        <meta name="description" content={course?.meta_description} />
        <link rel="canonical" href={`${SERVER}/courses/${course.slug}`} />
        <meta property="og:url" content={`${SERVER}/courses/${course.slug}`} />
        <meta
          property="og:title"
          content={`${course?.meta_title || course.name} | ${SITE_NAME}`}
        />
        <meta property="og:description" content={course?.meta_description} />
        <meta
          property="og:image"
          content={course?.og_image || COURSE_LIST_PAGE_OG_IMAGE}
        />
        {/* twitter tags */}
        <meta
          property="twitter:url"
          content={`${SERVER}/courses/${course.slug}`}
        />
        <meta
          name="twitter:title"
          content={course?.meta_title || course.name}
        />
        <meta name="twitter:description" content={course?.meta_description} />
        <meta
          name="twitter:image"
          content={course?.og_image || COURSE_LIST_PAGE_OG_IMAGE}
        />
      </Head>
      <HandleJoinCourse
        enroll_url={course?.enroll_url}
        user_enrolled={course?.user_enrolled}
      />
      {!course?.user_enrolled &&
        router.query?.enroll &&
        authService.isAuthenticated && (
          <PopUp
            snackbarProps={{
              open: true,
              message: router.query?.enroll ? "Enrolling..." : "Loading...",
              anchorOrigin: { horizontal: "center", vertical: "top" },
            }}
            variant="default"
          />
        )}
      <HeadSection course={course} />
      <Experts title="Our Alumni Work At" />
      <DetailSection course={course} />
      <EarnRecognition imgSrc={COURSE_CERTIFICATE_IMAGE} />
      <BottomBanner
        userEnrolled={!!course?.user_enrolled}
        slug={course.slug}
        enrollUrl={course.enroll_url}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  res,
  req,
  params,
}) => {
  try {
    res.setHeader(
      "Cache-Control",
      "public, s-maxage=10, stale-while-revalidate=59"
    );
    // If params is `null` return 404 page
    if (!params)
      return {
        notFound: true,
      };

    const service = new (
      await import("./../../src/common/service/baseServerSideService")
    ).default(req);
    const { data: course } = await service.get(
      `${SERVER}/api/courses/${params.slug}/`
    );
    // If there is no courses return 404 page
    if (!course || course.detail) {
      return {
        notFound: true,
      };
    }
    return {
      props: {
        course,
      },
    };
  } catch (error) {
    return onError(error);
  }
};
